import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, Layers, Award, CheckCircle2, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getCourseMeta } from '../data/university';

interface CourseData {
  title: string;
  description: string;
  technologies: string[];
  duration: string;
  prerequisites: string[];
  chapters: { title: string; syllabus: string[] }[];
  certificate: boolean;
}

const UniversityCoursePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('university');
  const categories = (t('categories', { returnObjects: true }) || []) as string[];
  const levels = (t('filters.levels', { returnObjects: true }) || []) as string[];
  const meta = id ? getCourseMeta(id) : undefined;
  const course = (t(`courses.${id}`, { returnObjects: true }) || null) as CourseData | null;
  const [openChapter, setOpenChapter] = useState<number>(0);
  const Icon = meta?.icon;

  if (!meta || !course) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 py-32 text-center">
          <div>
            <h1 className="text-2xl font-bold text-white mb-3">{t('notFound.title')}</h1>
            <button
              onClick={() => navigate('/university')}
              className="text-cyan-300 hover:text-white font-semibold inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('notFound.back')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-70" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/university')}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('backToCourses')}
          </button>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/25 to-cyan-400/10 border border-white/10 flex items-center justify-center">
              {Icon && <Icon className="h-7 w-7 text-cyan-300" />}
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-cyan-300 bg-cyan-400/[0.08] px-2.5 py-1 rounded-full">
              {categories[meta.categoryIndex]}
            </span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">{course.title}</h1>
          <p className="text-lg text-slate-400 max-w-3xl leading-relaxed mb-6">{course.description}</p>
          <div className="flex flex-wrap gap-5 text-sm text-slate-300 mb-8">
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-cyan-300" /> {t('labels.duration')}: {course.duration}
            </span>
            <span className="inline-flex items-center gap-2">
              <Layers className="h-4 w-4 text-cyan-300" /> {t('labels.chapters', { count: course.chapters.length })}
            </span>
            {course.certificate && (
              <span className="inline-flex items-center gap-2">
                <Award className="h-4 w-4 text-cyan-300" /> {t('labels.certificate')}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/training#training-request')}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-400 text-slate-950 px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            {t('labels.requestTraining')}
          </button>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
            <div>
              <h2 className="text-xl font-bold text-white mb-4">{t('labels.syllabus')}</h2>
              <div className="space-y-3">
                {course.chapters.map((chapter, index) => {
                  const isOpen = openChapter === index;
                  return (
                    <div key={index} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                      <button
                        onClick={() => setOpenChapter(isOpen ? -1 : index)}
                        className="w-full flex items-center justify-between p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/25 to-cyan-400/10 border border-white/10 flex items-center justify-center text-sm font-bold text-cyan-300">
                            {index + 1}
                          </span>
                          <span className="font-semibold text-white">{chapter.title}</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-cyan-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 pt-1 border-t border-white/5">
                          <ul className="space-y-2 mt-3">
                            {chapter.syllabus.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                                <CheckCircle2 className="h-4 w-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <h3 className="font-semibold text-white mb-4">{t('labels.technologies')}</h3>
                <div className="flex flex-wrap gap-2">
                  {course.technologies.map((tech) => (
                    <span key={tech} className="text-xs text-cyan-300 bg-cyan-400/[0.08] px-2.5 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              {course.prerequisites.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                  <h3 className="font-semibold text-white mb-4">{t('labels.prerequisites')}</h3>
                  <ul className="space-y-2">
                    {course.prerequisites.map((req) => (
                      <li key={req} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="h-4 w-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={() => navigate('/training#training-request')}
                className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 p-6 text-left hover:opacity-90 transition-opacity"
              >
                <span className="block text-sm font-semibold uppercase tracking-wide text-white/80 mb-1">{t('sidebar.eyebrow')}</span>
                <span className="block font-bold text-white">{t('sidebar.title')}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UniversityCoursePage;
